// Pure constants, utility functions, and types for PPAField.
// Kept separate from the component so they can be unit-tested and re-used
// without importing React.

import dayjs from 'dayjs';

import type { PPAInputConfig, PPASelectOption } from 'types/ppa';

// ---------------------------------------------------------------------------
// Exported constants
// ---------------------------------------------------------------------------

/** Fixed pixel width for all input controls so the form looks consistent. */
export const CONTROL_WIDTH = 300;

/** Maximum number of characters shown per tag in a multi-select field. */
export const MAX_TAG_CHARACTER_LIMIT = 7;

// ---------------------------------------------------------------------------
// Internal constants (used by hook & component via re-export)
// ---------------------------------------------------------------------------

export const PRESET_PERIOD_IDS = ['financial_year', 'current_year'] as const;

/** Page size for `get_attribute_names` (infinite scroll). */
export const ATTRIBUTE_NAMES_PAGE_SIZE = 25;

/**
 * Auto-fetch pages until this many options exist (helps when page size is tiny).
 * Set equal to `ATTRIBUTE_NAMES_PAGE_SIZE` so only the first page is prefetched.
 */
export const ATTRIBUTE_NAMES_PREFETCH_UNTIL_COUNT = ATTRIBUTE_NAMES_PAGE_SIZE;

/** Virtual list height: small when page size is small so the dropdown can scroll. */
export const ATTRIBUTE_NAMES_LIST_HEIGHT = ATTRIBUTE_NAMES_PAGE_SIZE <= 4 ? 56 : 256;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DayjsInput = string | number | Date | dayjs.Dayjs;

export type PresetOption = {
    dateArray?: [DayjsInput, DayjsInput];
    start_timestamp?: number;
    end_timestamp?: number;
};

export type PpaProjectFilterInclusion = {
    dimension?: string;
    attribute_type?: string;
    attribute_ex_id: string | string[];
    attribute_desc?: string;
};

export type PPAFieldProps = {
    fieldKey: string;
    inputConfig: PPAInputConfig;
    value: any;
    disabled?: boolean;
    onChange: (fieldKey: string, value: any) => void;
    /** Prior selected scopes for cascading filter requests (`filters.inclusion`). */
    priorProjectFilterInclusions?: PpaProjectFilterInclusion[];
    /** Inline validation (e.g. duplicate project name, simulation period rules). */
    validationMessage?: string;
    /** Tooltip when the control is disabled (e.g. cascading project filters). */
    disabledMessage?: string;
};

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

/**
 * Builds a unique form-state key for a field.
 * `project_filters` rows share the same `input_id`, so attribute_type/dimension
 * is appended to make the key unique.
 */
export function getFieldKey(inputConfig: PPAInputConfig, index: number): string {
    if (inputConfig.input_id === 'project_filters') {
        const uniqueSuffix = inputConfig.attribute_type ?? inputConfig.dimension ?? index;
        return `${inputConfig.input_id}_${uniqueSuffix}`;
    }
    return `${inputConfig.input_id}_${index}`;
}

/** Stable string id for select matching — avoids duplicate tags when edit payload uses numbers vs API strings. */
export function optionValueKey(value: unknown): string | null {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
}

/** Deduplicate multiselect values by string key; preserves first occurrence order. */
export function dedupeSelectValuesByKey<T>(values: T[]): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const v of values) {
        const k = optionValueKey(v);
        if (k == null || seen.has(k)) continue;
        seen.add(k);
        out.push(v);
    }
    return out;
}

/** Normalises the various shapes returned by `ppa/simulation/get_attribute_names`. */
export function parseAttributeNamesResponse(res: unknown): PPASelectOption[] {
    const r = res as Record<string, unknown>;
    const mapAttributeNameRow = (item: any) => ({
        label: item?.attribute_name ?? item?.label,
        value: item?.attribute_name ?? item?.value ?? item?.label,
    });

    if (Array.isArray(r?.values)) {
        return (r.values as any[]).map((item: any) => ({
            label: item?.label ?? item?.attribute_name,
            value: item?.value ?? item?.attribute_name,
        }));
    }
    if (Array.isArray(r) && r.length > 0 && (r as any[])[0]?.attribute_name != null) {
        return (r as any[]).map(mapAttributeNameRow);
    }
    if (Array.isArray(r?.data) && (r.data as any[]).length > 0 && (r.data as any[])[0]?.attribute_name != null) {
        return (r.data as any[]).map(mapAttributeNameRow);
    }
    if (Array.isArray(r?.attribute_names)) {
        return (r.attribute_names as string[]).map((name: string) => ({
            label: name,
            value: name,
        }));
    }
    return [];
}

function timestampToDayjs(ts: number): dayjs.Dayjs {
    return typeof ts === 'number' && ts > 1e12 ? dayjs(ts) : dayjs.unix(Number(ts));
}

/** Range shown beside the preset dropdown — same as CreateEditProjectContainer. */
export function getPresetDisplayRange(selectedOption: PresetOption | null | undefined): [unknown, unknown] | null {
    if (selectedOption?.dateArray?.[0] != null && selectedOption?.dateArray?.[1] != null) {
        return [selectedOption.dateArray[0], selectedOption.dateArray[1]];
    }
    const startTs = selectedOption?.start_timestamp;
    const endTs = selectedOption?.end_timestamp;
    if (startTs == null || endTs == null) return null;
    return [timestampToDayjs(startTs), timestampToDayjs(endTs)];
}

/** JSON stored on the field for payload / validation — mirrors CreateEditProjectContainer when `dateArray` exists. */
export function getPresetSimulationSelectionJson(selectedOption: PresetOption | null | undefined): string | undefined {
    if (selectedOption?.dateArray?.[0] != null && selectedOption?.dateArray?.[1] != null) {
        const utcOffset = dayjs().utcOffset();
        const startDateUnix = dayjs(selectedOption.dateArray[0])
            .add(utcOffset, 'm')
            .subtract(6, 'day')
            .unix();
        const endDateUnix = dayjs(selectedOption.dateArray[1])
            .add(utcOffset, 'm')
            .subtract(6, 'day')
            .unix();
        return JSON.stringify({
            start_timestamp: startDateUnix,
            end_timestamp: endDateUnix,
        });
    }
    if (selectedOption?.start_timestamp !== undefined && selectedOption?.end_timestamp !== undefined) {
        return JSON.stringify({
            start_timestamp: selectedOption.start_timestamp,
            end_timestamp: selectedOption.end_timestamp,
        });
    }
    return undefined;
}

/** Shown when a cascading project-filter dropdown is disabled until its parent is selected. */
export const PPA_CASCADING_FILTER_DISABLED_MSG =
    'Please select the previous field to enable this dropdown.';

export function getPpaCascadingFilterDisabledMessage(parentInputLabel?: string): string {
    const label = parentInputLabel?.trim();
    if (label) {
        return `Please select ${label} before selecting this field.`;
    }
    return PPA_CASCADING_FILTER_DISABLED_MSG;
}
